import { Moon, Sun, type LucideIcon } from 'lucide-react'
import { setSettings as setPersistedSettings, useSettings } from '@axilon-apps/libs-ui-dashboard'
import type { ColorScheme, Settings } from '@axilon-apps/libs-ui-dashboard'
import { Button } from '../../../components/ui/button'
import { Card, CardContent } from '../../../components/ui/card'

const MODES: ReadonlyArray<{
  value: ColorScheme
  label: string
  icon: LucideIcon
}> = [
  { value: 'light', label: 'Light', icon: Sun },
  { value: 'dark', label: 'Dark', icon: Moon }
]

/** Light/dark toggle buttons — shared by ThemeModeCard and WorkspaceCard. */
export function ThemeModeToggle() {
  // Joy's `useColorScheme` is gone — the dashboard ThemeProvider drives
  // every selector hook (`data-theme`, `class="dark"`, `color-scheme`)
  // off `settings.colorScheme` via a useEffect, so writing through
  // setSettings is enough to flip the theme.
  const { settings, setSettings } = useSettings()
  const colorScheme = settings.colorScheme

  const handleToggle = (next: ColorScheme) => {
    const updated = { ...settings, colorScheme: next } satisfies Settings
    setPersistedSettings(updated)
    setSettings(updated)
  }

  return (
    <div className="flex gap-1.5">
      {MODES.map(({ value, label, icon: Icon }) => {
        const active = colorScheme === value
        return (
          <Button
            key={value}
            type="button"
            variant={active ? 'secondary' : 'outline'}
            size="xs"
            aria-pressed={active}
            onClick={() => handleToggle(value)}
          >
            <Icon className="size-3.5" strokeWidth={active ? 2.5 : 1.75} />
            {label}
          </Button>
        )
      })}
    </div>
  )
}

export function ThemeModeCard() {
  return (
    <Card className="py-2.5">
      <CardContent className="flex items-center justify-between gap-3">
        <h3 className="text-sm font-semibold text-ink">Theme</h3>
        <ThemeModeToggle />
      </CardContent>
    </Card>
  )
}
